import React from "react";
import filesize from "filesize";
import config from "config";
import { AttachedFileData, AttachedFileError } from "types";
import "./style.scss";
import BaseButton from "components/BaseButton";
import BaseIcon from "components/BaseIcon";
import { useTranslation } from "locales";

interface Props {
  className?: string;
  attachedFileData: AttachedFileData | AttachedFileError;
  removeAttachment?: (id: string) => void;
  link?: string;
  showPreview?: boolean;
}

function MessageAttachView({ className, attachedFileData, removeAttachment, link, showPreview = true }: Props) {
  const { t } = useTranslation();
  const cls = ["file-attached"];
  if (className) cls.push(className);

  if ("error" in attachedFileData) {
    cls.push(" __error");
    return (
      <div className={cls.join(" ")}>
        <span className="file-attached__message label">{attachedFileData.error}</span>
        {removeAttachment && (
          <BaseButton
            className="file-attached__remove"
            small={true}
            icon={true}
            opacity={true}
            name="close-error"
            onClick={() => removeAttachment(attachedFileData.id)}
          >
            <BaseIcon size={18}>close</BaseIcon>
          </BaseButton>
        )}
      </div>
    );
  }
  const fileNameParts = attachedFileData.fileName.split(".");
  const extension = (fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1] : "").toLocaleLowerCase();
  const typeCls = ["file-attached__type mr-1"];
  if (extension === "doc" || extension === "docx") {
    typeCls.push("__doc");
  } else if (config.fileTypes.image.includes(extension)) {
    typeCls.push("__image");
  } else if (config.fileTypes.video.includes(extension)) {
    typeCls.push("__video");
  } else if (config.fileTypes.audio.includes(extension)) {
    typeCls.push("__audio");
  } else if (config.fileTypes.document.includes(extension)) {
    typeCls.push("__document");
  }
  const size = filesize(attachedFileData.size);

  const content = (
    <>
      {showPreview && attachedFileData.src && (
        <img className="file-attached__image mb-1" src={attachedFileData.src} alt={attachedFileData.fileName} />
      )}
      <div className="file-attached__info">
        {extension && <div className={typeCls.join(" ")}>{extension}</div>}
        <div className="file-attached__name mr-2" title={attachedFileData.fileName}>
          {attachedFileData.fileName}
        </div>
        <div className="file-attached__size">{size}</div>
        {attachedFileData.uploadProgress !== 100 && (
          <div
            className="file-attached__progress ml-2"
            style={{ backgroundSize: `${attachedFileData.uploadProgress}% 100%` }}
          />
        )}
      </div>
    </>
  );

  return link ? (
    <div className={cls.join(" ") + " __with-download"}>
      <a className="file-attached__open-link" href={link} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
      <a
        className="file-attached__download-btn"
        href={link}
        download={attachedFileData.fileName}
        title={t("Download")}
        onClick={(e) => e.stopPropagation()}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor" />
        </svg>
      </a>
    </div>
  ) : (
    <div className={cls.join(" ")}>
      {content}
      {/* Composer usage (no `link`): per-file remove. Cancels the in-flight
          upload too (removeAttachment handles removeFromQueue for < 100%). */}
      {removeAttachment && (
        <button
          type="button"
          className="file-attached__remove-file"
          title={t("Remove")}
          onClick={() => removeAttachment(attachedFileData.id)}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

export default MessageAttachView;
